import React, { useCallback, useState } from "react";
import { Modal, Button } from "react-bootstrap";

/**
 * ConfirmHost + global confirm() — drop-in replacement for window.confirm.
 *
 * Mount <ConfirmHost /> once near the app root (in App.js next to
 * <Router>). Anywhere in the tree, call:
 *
 *   const ok = await confirm({
 *     title: 'Approve?',
 *     message: 'This unlocks Buy Numbers immediately.',
 *     confirmLabel: 'Approve',
 *     variant: 'success',   // or 'danger', 'primary'…
 *   });
 *   if (!ok) return;
 *
 * The bare-string form works too: `await confirm('Delete this?')`.
 *
 * If ConfirmHost hasn't mounted yet (e.g. an alert fires before the
 * tree is ready), we fall back to the native browser confirm — better
 * than the promise never resolving.
 */

let hostConfirm = null;

export const confirm = (opts) => {
    const o = typeof opts === "string" ? { message: opts } : opts;
    if (!hostConfirm) {
        // eslint-disable-next-line no-alert
        return Promise.resolve(window.confirm(o.message));
    }
    return hostConfirm(o);
};

export function ConfirmHost() {
    const [pending, setPending] = useState(null);

    hostConfirm = useCallback(
        (opts) =>
            new Promise((resolve) => {
                setPending({ ...opts, resolve });
            }),
        []
    );

    const close = (ok) => {
        if (!pending) return;
        pending.resolve(ok);
        setPending(null);
    };

    if (!pending) {
        return null;
    }

    const {
        title = "Are you sure?",
        message = "",
        confirmLabel = "Confirm",
        cancelLabel = "Cancel",
        variant = "primary",
    } = pending;

    return (
        <Modal
            show
            onHide={() => close(false)}
            centered
            backdrop="static"
            keyboard
        >
            <Modal.Header closeButton>
                <Modal.Title as="h5">{title}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <p className="mb-0" style={{ whiteSpace: "pre-line" }}>
                    {message}
                </p>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="outline-secondary" onClick={() => close(false)}>
                    {cancelLabel}
                </Button>
                <Button
                    variant={variant}
                    onClick={() => close(true)}
                    autoFocus
                >
                    {confirmLabel}
                </Button>
            </Modal.Footer>
        </Modal>
    );
}
